import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuditTrail } from '../../../services/auditTrailApi';
import type { FetchAuditTrailParams } from './auditTrailTypes';
import { groupAuditEventsBySession, groupSessionsByActor, type AuditActorGroup } from '../../../../audit/auditSessionGrouping';

export interface AuditTimelineFilters {
  actorId: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

function toIsoStart(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  return x.toISOString();
}

function toIsoEnd(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return x.toISOString();
}

const TIMELINE_FETCH_LIMIT = 100;

export function useAuditTrailTimelineQuery(
  projectId: number | undefined,
  filters: AuditTimelineFilters
) {
  const [debouncedActorId, setDebouncedActorId] = useState('');
  const [groups, setGroups] = useState<AuditActorGroup[]>([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedActorId(filters.actorId.trim()), 300);
    return () => window.clearTimeout(t);
  }, [filters.actorId]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const params: FetchAuditTrailParams = {
      limit: TIMELINE_FETCH_LIMIT,
      offset: 0,
      sortBy: 'created_at',
      sortOrder: 'DESC',
      signal: controller.signal,
    };

    if (projectId != null && Number.isFinite(projectId)) {
      params.project_id = projectId;
    }
    if (debouncedActorId) params.actor_id = debouncedActorId;
    if (filters.dateFrom) params.created_from = toIsoStart(filters.dateFrom);
    if (filters.dateTo) params.created_to = toIsoEnd(filters.dateTo);

    try {
      const result = await fetchAuditTrail(params);

      if (controller.signal.aborted) return;

      if (!result.ok) {
        if (result.error === 'Request cancelled') return;
        setGroups([]);
        setSessionCount(0);
        setTotal(0);
        setError(result.error);
        return;
      }

      const sessionGroups = groupAuditEventsBySession(result.data.items);
      const actorGroups = groupSessionsByActor(sessionGroups);
      setGroups(actorGroups);
      setSessionCount(sessionGroups.length);
      setTotal(result.data.total);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [projectId, filters.dateFrom, filters.dateTo, debouncedActorId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return {
    actorGroups: groups,
    sessionCount,
    total,
    loading,
    error,
    reload: load,
    isActorFiltered: Boolean(debouncedActorId),
    isDateFiltered: Boolean(filters.dateFrom || filters.dateTo),
  };
}
